import { Button, Tag } from 'antd';
import { FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { ComplianceDocumentHistory } from '../../components/ComplianceDocumentHistory';
import { DOCUMENT_STATUS_LABELS } from '../../components/document-status-labels';
import { EmptyState } from '../../components/EmptyState';
import type { ComplianceDocumentView } from '../../lib/compliance-documents';
import { TruckDocumentModal } from './TruckDocumentModal';
import type { DocumentFormInput } from './use-truck-documents';

interface TruckDocumentsProps {
  documents: ComplianceDocumentView[];
  canManage: boolean;
  submitting: boolean;
  onAdd: (input: DocumentFormInput, isRenewal: boolean) => Promise<boolean>;
  onOpen: (documentId: string) => Promise<void>;
}

/** A truck's compliance documents (gitaumoses4/chukua#17): one row per active document type, each showing its current document or "missing" — attach and renew only, never edit or remove. */
export function TruckDocuments({ documents, canManage, submitting, onAdd, onOpen }: TruckDocumentsProps) {
  const [acting, setActing] = useState<ComplianceDocumentView | null>(null);

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-fg">Documents</h2>
      {documents.length === 0 && (
        <EmptyState icon={FileText} title="No document types yet" description="Add document types for trucks in fleet reference data." />
      )}
      {documents.length > 0 && (
        <ul className="flex flex-col gap-3">
          {documents.map((document) => (
            <DocumentTypeRow key={document.documentTypeId} document={document} canManage={canManage} onAct={() => setActing(document)} onOpen={onOpen} />
          ))}
        </ul>
      )}
      <TruckDocumentModal
        key={acting?.documentTypeId ?? 'closed'}
        open={acting !== null}
        documentType={acting}
        submitting={submitting}
        onSubmit={async (input, isRenewal) => {
          const saved = await onAdd(input, isRenewal);
          if (saved) setActing(null);
          return saved;
        }}
        onCancel={() => setActing(null)}
      />
    </section>
  );
}

function DocumentTypeRow({
  document,
  canManage,
  onAct,
  onOpen,
}: {
  document: ComplianceDocumentView;
  canManage: boolean;
  onAct: () => void;
  onOpen: (documentId: string) => Promise<void>;
}) {
  const status = DOCUMENT_STATUS_LABELS[document.status];
  const current = document.current;
  const history = document.history.filter((entry) => entry.id !== current?.id);

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          {current ? (
            <button type="button" className="font-medium text-link hover:underline" onClick={() => onOpen(current.id)}>
              {document.documentTypeName}
            </button>
          ) : (
            <p className="font-medium text-fg">{document.documentTypeName}</p>
          )}
          <p className="text-sm text-muted">{current ? `Expires ${new Date(current.expiryDate).toLocaleDateString()}` : 'No document attached'}</p>
        </div>
        <div className="flex items-center gap-3">
          <Tag color={status.color}>{status.label}</Tag>
          {canManage && (
            <Button size="small" icon={<Plus className="size-3.5" />} onClick={onAct}>
              {current ? 'Renew' : 'Add'}
            </Button>
          )}
        </div>
      </div>
      <ComplianceDocumentHistory entries={history} onOpen={onOpen} />
    </li>
  );
}
